// ThemeGrid.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody,
  Spinner,
  UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem
} from "reactstrap";
import { withTranslation } from "react-i18next";

import axiosInstance from "utils/axiosInstance";

import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";
import StatBox from "components/Common/StatBox";
import BorderTrailBox from 'components/Common/BorderTrailBox';
import ExpandingCircleMenu from '../components/ExpandingCircleMenu';
import Toast from "components/Common/Toast"; 

import "./ThemeGrid.css";

import home4 from "assets/images/info/home4.png";

import theme1Img from "assets/images/info/themes/theme1.png";
import theme2Img from "assets/images/info/themes/theme2.png";
import theme3Img from "assets/images/info/themes/theme3.png";
import theme4Img from "assets/images/info/themes/theme4.png";
import theme5Img from "assets/images/info/themes/theme5.png";
import theme6Img from "assets/images/info/themes/theme6.png";
import theme7Img from "assets/images/info/themes/theme7.png";
import theme8Img from "assets/images/info/themes/theme8.png";

import placeholder_image from "assets/images/info/card-placeholder-medium.png";

import VipBadge from "../components/VipBadge";

const themes = [
  { id: 1, name: "學習與工作技能", route: "/myInfo/themes/67f7ea08240e8390d3a8bc0e", img: theme1Img },
  { id: 2, name: "影劇及娛樂", route: "/myInfo/themes/67f7ea09240e8390d3a8bc11", img: theme2Img },
  { id: 3, name: "觀光與特殊體驗", route: "/myInfo/themes/6800f4d524ac18d2fafbd454", img: theme3Img },
  { id: 4, name: "日常生活", route: "/myInfo/themes/6800f4d624ac18d2fafbd457", img: theme4Img },
  { id: 5, name: "服飾與配件", route: "/myInfo/themes/6800f4d624ac18d2fafbd45a", img: theme5Img },
  { id: 6, name: "人物", route: "/myInfo/themes/6800f4d724ac18d2fafbd45d", img: theme6Img },
  { id: 7, name: "消費與投資", route: "/myInfo/themes/67f7e43b8726052353db7944", img: theme7Img },
  { id: 8, name: "食物", route: "/myInfo/themes/6800f4e824ac18d2fafbd468", img: theme8Img },
  { id: 9, name: "問題", route: "/myInfo/themes/6800f51e24ac18d2fafbd539", img: placeholder_image },
  { id: 10, name: "特別的我", route: "/myInfo/themes/6800f51f24ac18d2fafbd53c", img: placeholder_image },
];


// ────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────

const ThemeGrid = ({ t }) => {

  document.title = "選擇主題類別 | TheChart.AI";
  const navigate = useNavigate();


  /* ---------- Toast ---------- */
  const [toasts, setToasts] = useState([]);   // { id, msg, type, dur }
  const pushToast = (msg, type = "success", duration = 3000) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type, duration }]);
  };
  const popToast = id => setToasts(t => t.filter(x => x.id !== id));


  // ────────────────────────────────────────────────────
  // Fetch states
  // ────────────────────────────────────────────────────
  const [myInfoPoint, setMyInfoPoint] = useState(null);
  const [myInfoProgress, setMyInfoProgress] = useState(null);

  const fetchUserPoints = async () => {
    try {
      const { data } = await axiosInstance.get("/knovia/myInfo/infoPoints");
      console.log('[fetchUserPoints]', data);
      setMyInfoPoint(data.totalPoints);
    } catch (err) {
      console.error(err);
      pushToast(t("無法取得積分，請稍後再試"), "error");
    }
  };

  const fetchUserProgress = async () => {
    try {
      const { data } = await axiosInstance.get("/knovia/myInfo/infoProgress");
      console.log('[fetchUserProgress]', data);
      setMyInfoProgress(data);
    } catch (err) {
      console.error(err);
      pushToast(t("無法取得進度資訊，請稍後再試"), "error");
    }
  };

  useEffect(() => {
    fetchUserPoints(); 
    fetchUserProgress();
  },[])


  // ────────────────────────────────────────────────────
  // Theme Rows
  // ────────────────────────────────────────────────────
  const renderRows = () => {
    const rows = [];
    for (let i = 0; i < themes.length; i += 2) {
      rows.push(
        <Row className="theme-row" key={i}>
          {[0, 1].map((j) => (
            <Col lg="6" key={themes[i + j].id}
              className={`theme-card-outer-col ${i < 4 ? 'top' : 'bottom'} ${j === 0 ? 'left' : 'right'}`}
            >
              <div
                className={`theme-card ${i < 4 ? 'top' : 'bottom'} ${j === 0 ? 'left' : 'right'}`}
                onClick={() => {
                  navigate(themes[i + j].route, {
                    state: { themeName: themes[i + j].name }
                  })
                }}
              >
                {j === 0 ? (
                  <>
                    {/* Left Column (Text/Img) */}
                    <div className="theme-text">{themes[i + j].name}</div>
                    <div className="theme-image">
                      <img src={themes[i + j].img} alt="theme" />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Right Column (Img/Text) */}
                    <div className="theme-image flipped">
                      <img src={themes[i + j].img} alt="theme" />
                    </div>
                    <div className="theme-text flipped">{themes[i + j].name}</div>
                  </>
                )}

                {/* Gloss Effect */}
                <div className="theme-gloss" />

              </div>
            </Col>
          ))}
        </Row>
      );
    }
    return rows;
  };

  const [selectedUnitType, setSelectedUnitType] = useState("主題");
  const [isProgressDropdownOpen, setIsProgressDropdownOpen] = useState(false);

  // ────────────────────────────────────────────────────
  // Return 
  // ────────────────────────────────────────────────────

  return (
    <>
    <div className="page-content theme-grid"
      style={{ backgroundImage: `url(${home4})` }}
    >
      <ExpandingCircleMenu />
      {/* toast stack */}
      {toasts.map(t => (
        <Toast
          key={t.id}
          message={t.msg}
          type={t.type}
          duration={t.duration}
          onClose={() => popToast(t.id)}
        />
      ))}

      <Container fluid>

        <BreadcrumbAboveImage
          title={t("My Info")}
          breadcrumbItems={[
            { title: t("我的 AI 應用主頁"), to: "/myInfo/dashboard" },
            { title: t("選擇主題類別") }
          ]}
        />

        <Card className="theme-grid-card">
          <CardBody>

            {/*
            <Row style={{ margin:"0.5rem 0rem" }}>
              <Col xs={12} className="d-flex justify-content-end">
                <div className="d-flex justify-content-center align-items-center" 
                  style={{ marginRight: "5%" }}
                >
                  <div className="d-flex justify-content-center align-items-center">
                    <img src={vip1} alt="vip" 
                      style={{ maxHeight: '60px', maxWidth: '60px' }}
                    />
                  </div>
                  <div className="vip-text-div d-flex justify-content-center align-items-center" 
                    style={{ margin: '0px 5px 0px 2px'}}
                  >
                    <p className="mb-0" style={{ fontSize: '16px' }}>VIP1</p>
                  </div>
                </div>
              </Col>
            </Row>
            */}

            <Row style={{ margin:"0.5rem 0rem" }}>
              <Col xs={12} className="d-flex justify-content-end">
                <VipBadge vipLevel="VIP1" />
              </Col>
            </Row>
            
            <Row style={{ paddingBottom:"1rem" }}>

              {/* Point Box */}
              <Col xs={12} sm={6}>
                <div className="border-trail-box-outer-div" style={{ display: 'flex', justifyContent: 'center' }}>
                  <BorderTrailBox
                    className="border-trail-box"
                    //trailColor="#000000"
                    trailColor="gold"
                    //borderColor="#000000"
                    borderRadius={38}
                    borderWidth={2}
                    trailSize={105}
                    speed={2.5}
                    mainStyles={{ display: "flex", justifyContent: "center" }}
                    asideStyles={{ maxWidth: "50%" }}
                  >
                    <div style={{ 
                      padding: "1.6rem 1rem 1.3rem 1rem", 
                      backgroundColor: "white",
                      borderRadius: "38px"
                    }}>
                      <Row style={{ marginBottom: "0.3rem" }}>
                        <Col xs={12}>
                          <p style={{ margin:'0', fontSize:'20px' }}>{t("目前積分點數")}</p>
                          <div className="mt-1 mb-2">
                            {myInfoPoint !== null ? (
                              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "62px", maxHeight: "62px" }}>
                                <StatBox value={myInfoPoint || 0} loading={false} numberStyles={{ color:"gold", fontWeight: "400" }} />
                              </div>
                            ) : (
                              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "62px", maxHeight: "62px" }}>
                                <Spinner size="sm" />
                              </div>
                            )}
                          </div>
                          <h6 style={{ textAlign: "center" }}>({t("每")} 30 {t("積分")} = 1 USD)</h6>
                        </Col>
                      </Row>
                    </div>
                  </BorderTrailBox>
                </div>
              </Col>

              {/* Progress Box */}
              <Col xs={12} sm={6}>
                <div className="border-trail-box-outer-div" style={{ display: 'flex', justifyContent: 'center' }}>
                  <BorderTrailBox
                    className={`border-trail-box ${isProgressDropdownOpen ? "no-hover-scale" : ""}`}
                    //trailColor="#000000"
                    trailColor="#03a9f4"
                    //borderColor="#000000"
                    borderRadius={38}
                    borderWidth={2}
                    trailSize={105}
                    speed={2.5}
                    mainStyles={{ display: "flex", justifyContent: "center" }}
                    asideStyles={{ maxWidth: "50%" }}
                  >
                    <div style={{ 
                      padding: "1.6rem 1rem 1.3rem 1rem", 
                      backgroundColor: "white",
                      borderRadius: "38px"
                    }}>
                      <Row style={{ marginBottom: "0.3rem" }}>
                        <Col xs={12}>
                          <p style={{ margin:'0', fontSize:'20px' }}>{t("目前填答進度")}</p>
                          <div className="mt-1 mb-2">
                            {selectedUnitType !== null ? (
                              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "62px", maxHeight: "62px" }}>
                                {selectedUnitType === "主題" && (
                                  <StatBox value={3} loading={false} numberStyles={{ color:"#03a9f4", fontWeight: "400" }} />
                                )}
                                {selectedUnitType === "題組" && (
                                  <StatBox value={5} loading={false} numberStyles={{ color:"#03a9f4", fontWeight: "400" }} />
                                )}
                                {selectedUnitType === "問題" && (
                                  <StatBox value={50} loading={false} numberStyles={{ color:"#03a9f4", fontWeight: "400" }} />
                                )}
                                <p style={{ fontSize: "2.5rem", fontWeight: "300", color: "#03a9f4", margin: "0px 0px 5px 2px" }}>
                                  {`/`}
                                </p>
                                {selectedUnitType === "主題" && myInfoProgress?.topicCount && (
                                  <p style={{ fontSize: "3rem", fontWeight: "400", color:"#03a9f4", marginBottom: "5px" }}>
                                    {myInfoProgress.topicCount}
                                  </p>
                                )}
                                {selectedUnitType === "題組" && myInfoProgress?.questionSetCount && (
                                  <p style={{ fontSize: "3rem", fontWeight: "400", color:"#03a9f4", marginBottom: "5px" }}>
                                    {myInfoProgress.questionSetCount}
                                  </p>
                                )}
                                {selectedUnitType === "問題" && myInfoProgress?.questionCount && (
                                  <p style={{ fontSize: "3rem", fontWeight: "400", color:"#03a9f4", marginBottom: "5px" }}>
                                    {myInfoProgress.questionCount}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "62px", maxHeight: "62px" }}>
                                <Spinner size="sm" />
                              </div>
                            )}
                          </div>

                          <UncontrolledDropdown className="text-center theme-grid-dropdown" 
                            onToggle={() => setIsProgressDropdownOpen(prev => !prev)}
                          >
                            <DropdownToggle
                              tag="div"
                              style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "4px" }}
                            >
                              <h6 style={{ margin: 0 }}>{t(`個${selectedUnitType}` || "個主題")}</h6>
                              <span style={{ fontSize: "0.8em" }}>▼</span>
                            </DropdownToggle>
                            <DropdownMenu style={{ width: "120px" }}>
                              <DropdownItem onClick={() => setSelectedUnitType("主題")}>{t("主題")}</DropdownItem>
                              <DropdownItem onClick={() => setSelectedUnitType("題組")}>{t("題組")}</DropdownItem>
                              <DropdownItem onClick={() => setSelectedUnitType("問題")}>{t("問題")}</DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>

                        </Col>
                      </Row>
                    </div>
                  </BorderTrailBox>
                </div>
              </Col>

            </Row>

            {/* Theme List */}
            <Row>
              <Col xs={12}>
                <div className="theme-grid-outer-div">
                  {renderRows()}
                </div>
              </Col>
            </Row>

          </CardBody>
        </Card>
      </Container>
    </div>
    </>
  );
};

export default withTranslation()(ThemeGrid);


